import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StageFormModal } from "./StageFormModal";
import {
  Loader2,
  Plus,
  Trash2,
  AlertTriangle,
  AlertCircle,
  Edit,
  ArrowUp,
  ArrowDown,
  Layers,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  opportunityAPI,
  OpportunityStage,
  StageStagnationConfig,
  StageVisibilityConfig,
} from "@/services/opportunityService";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface CreateStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  pipelineId?: string;
  onStageCreated?: () => void;
  onStageUpdated?: () => void;
  onStageDeleted?: () => void;
}

const STAGE_TYPE_TO_LEVEL: Record<string, number> = {
  start: 0,
  progress: 1,
  closed_won: 2,
  closed_lost: 2,
  custom: 1,
};

const LEVEL_TO_STAGE_TYPES: Record<number, string[]> = {
  0: ["start"],
  1: ["progress", "custom"],
  2: ["closed_won", "closed_lost"],
};

const LEVEL_GROUPS = [
  { label: "Inicial", level: 0, stage_types: ["start"] },
  { label: "En Progreso", level: 1, stage_types: ["progress", "custom"] },
  { label: "Finalizada", level: 2, stage_types: ["closed_won", "closed_lost"] },
];

const getStageTypeFromLevel = (level: number, stageName?: string): string => {
  const name = stageName?.toLowerCase() || "";
  const types = LEVEL_TO_STAGE_TYPES[level];

  if (level === 2) {
    // Determinar si es ganada o perdida según el nombre
    return name.includes("perdida") || name.includes("lost") ? "closed_lost" : "closed_won";
  }

  return types?.[0] || "progress";
};

const getStageButtonText = (stageType: string): string => {
  switch (stageType) {
    case "start":
      return "Inicial";
    case "progress":
    case "custom":
      return "En progreso";
    case "closed_won":
      return "Finalizada";
    case "closed_lost":
      return "Perdida";
    default:
      return "En progreso";
  }
};

export function CreateStageModal({
  isOpen,
  onClose,
  slug,
  pipelineId,
  onStageCreated,
  onStageUpdated,
  onStageDeleted,
}: CreateStageModalProps) {
  const { toast } = useToast();
  const [stages, setStages] = useState<OpportunityStage[]>([]);
  const [loading, setLoading] = useState({
    stages: false,
    create: false,
    delete: false,
  });
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<OpportunityStage | null>(
    null,
  );
  const [selectedGroupLevel, setSelectedGroupLevel] = useState<number>(0);
  const [stageToDelete, setStageToDelete] = useState<OpportunityStage | null>(
    null,
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [selectedStageForRules, setSelectedStageForRules] =
    useState<OpportunityStage | null>(null);

  useEffect(() => {
    if (isOpen && slug) {
      fetchStages();
    }
  }, [isOpen, slug, pipelineId]);

  const fetchStages = async () => {
    try {
      setLoading((prev) => ({ ...prev, stages: true }));
      const response = await opportunityAPI.getStages({
        slug,
        is_active: true,
        pipeline_id: pipelineId,
      });
      setStages(
        response.data.sort((a, b) => a.order_position - b.order_position),
      );
    } catch (error: any) {
      toast({
        title: "Error al cargar etapas",
        description: error.message || "No se pudieron cargar las etapas",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, stages: false }));
    }
  };

  const handleOpenFormModal = (level: number) => {
    setSelectedGroupLevel(level);
    setEditingStage(null);
    setIsFormModalOpen(true);
  };

  const handleEditStage = (stage: OpportunityStage) => {
    setEditingStage(stage);
    setIsFormModalOpen(true);
  };

  const handleSaveStage = async (stageData: {
    name: string;
    description?: string;
    color: string;
    level: number;
    orden: number;
    stagnation_config?: StageStagnationConfig | null;
    visibility_config?: StageVisibilityConfig | null;
  }) => {
    console.log("💾 handleSaveStage ejecutado", {
      stageData,
      pipelineId,
      isEdit: !!editingStage,
    });

    try {
      setLoading((prev) => ({ ...prev, create: true }));

      const finalStageType = getStageTypeFromLevel(
        stageData.level,
        stageData.name,
      );

      console.log(
        `[DEBUG] Determined stage type: ${finalStageType} for stage name "${stageData.name}"`,
      );

      if (editingStage) {
        const updateData = {
          slug: slug,
          name: stageData.name.trim(),
          color: stageData.color,
          stage_type: finalStageType,
          order_position: stageData.orden,
          is_active: true,
          description: stageData.description?.trim(),
          pipeline_id: pipelineId,
          stagnation_config: stageData.stagnation_config,
          visibility_config: stageData.visibility_config,
        };

        console.log("🔄 Actualizando etapa con datos:", updateData);

        const response = await opportunityAPI.updateStage(
          editingStage.id,
          updateData,
        );

        toast({
          title: "¡Etapa actualizada!",
          description: `La etapa "${stageData.name}" se ha actualizado correctamente`,
        });

        onStageUpdated?.();

        setStages((prev) =>
          prev.map((s) => (s.id === editingStage.id ? response.data : s)),
        );
      } else {
        if (!pipelineId) {
          console.error(
            "❌ Error: pipelineId es undefined o null al intentar crear etapa",
          );
          toast({
            title: "Error",
            description: "No se puede crear una etapa sin un pipeline asignado",
            variant: "destructive",
          });
          return;
        }

        const nextOrderPosition =
          stages.length > 0
            ? Math.max(...stages.map((s) => s.order_position)) + 1
            : 1;

        const createData = {
          slug,
          name: stageData.name.trim(),
          color: stageData.color,
          order_position: nextOrderPosition,
          stage_type: finalStageType,
          pipeline_id: pipelineId,
          is_active: true,
          description: stageData.description?.trim(),
          stagnation_config: stageData.stagnation_config,
          visibility_config: stageData.visibility_config,
        };

        console.log("🚀 Enviando solicitud de creación de etapa:", createData);

        const response = await opportunityAPI.createStage(createData);

        console.log("✅ Etapa creada exitosamente:", response.data);

        toast({
          title: "¡Etapa creada!",
          description: `La etapa "${stageData.name}" se ha creado correctamente`,
        });

        onStageCreated?.();

        setStages((prev) =>
          [...prev, response.data].sort(
            (a, b) => a.order_position - b.order_position,
          ),
        );
      }

      setIsFormModalOpen(false);
      setEditingStage(null);
    } catch (error: any) {
      console.error("❌ Error en handleSaveStage:", error);
      toast({
        title: editingStage
          ? "Error al actualizar etapa"
          : "Error al crear etapa",
        description: error.message || "Ha ocurrido un error",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const confirmDeleteStage = (stage: OpportunityStage) => {
    setStageToDelete(stage);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;

    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      await opportunityAPI.deleteStage(stageToDelete.id, slug);

      toast({
        title: "¡Etapa eliminada!",
        description: "La etapa se ha eliminado correctamente",
      });

      onStageDeleted?.();

      setStages((prev) => prev.filter((s) => s.id !== stageToDelete.id));
      setIsDeleteConfirmOpen(false);
      setStageToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error al eliminar etapa",
        description: error.message || "No se pudo eliminar la etapa",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleMoveStage = async (
    stage: OpportunityStage,
    direction: "up" | "down",
  ) => {
    const group = LEVEL_GROUPS.find((g) =>
      g.stage_types.includes(stage.stage_type),
    );
    if (!group) return;

    const groupStages = stages
      .filter((s) => group.stage_types.includes(s.stage_type))
      .sort((a, b) => a.order_position - b.order_position);

    const currentIndex = groupStages.findIndex((s) => s.id === stage.id);
    if (currentIndex === -1) return;

    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const targetStage = groupStages[targetIndex];

    if (!targetStage) return;

    try {
      // Optimistic update
      const newStages = stages.map((s) => {
        if (s.id === stage.id)
          return { ...s, order_position: targetStage.order_position };
        if (s.id === targetStage.id)
          return { ...s, order_position: stage.order_position };
        return s;
      });

      setStages(newStages.sort((a, b) => a.order_position - b.order_position));

      await Promise.all([
        opportunityAPI.updateStage(stage.id, {
          slug,
          order_position: targetStage.order_position,
        }),
        opportunityAPI.updateStage(targetStage.id, {
          slug,
          order_position: stage.order_position,
        }),
      ]);

      onStageUpdated?.();
    } catch (error: any) {
      console.error("Error moving stage:", error);
      fetchStages();
      toast({
        title: "Error",
        description: "No se pudo mover la etapa",
        variant: "destructive",
      });
    }
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingStage(null);
  };

  const groupedStages = LEVEL_GROUPS.map((group) => ({
    ...group,
    stages: stages
      .filter((s) => group.stage_types.includes(s.stage_type))
      .sort((a, b) => a.order_position - b.order_position),
  }));

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">

          {/* Header */}
          <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex-shrink-0">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                    <Layers className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                      Gestión de Etapas
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-0.5">
                      Organiza y configura las etapas del pipeline
                    </DialogDescription>
                  </div>
                </div>
                {loading.stages && (
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                )}
              </div>
            </DialogHeader>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
            <div className="space-y-5">
              {groupedStages.map((group) => {
                const groupColors: Record<number, { bg: string; badge: string; dot: string; btn: string }> = {
                  0: { bg: "bg-blue-50/60 border-blue-100/80", badge: "bg-blue-100 text-blue-700", dot: "bg-blue-500", btn: "border-blue-200 text-blue-600 hover:bg-blue-50" },
                  1: { bg: "bg-indigo-50/60 border-indigo-100/80", badge: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500", btn: "border-indigo-200 text-indigo-600 hover:bg-indigo-50" },
                  2: { bg: "bg-slate-50/80 border-slate-200/60", badge: "bg-slate-100 text-slate-700", dot: "bg-slate-500", btn: "border-slate-200 text-slate-600 hover:bg-slate-100" },
                };
                const colors = groupColors[group.level] || groupColors[1];

                return (
                  <div key={group.level} className={`rounded-[20px] border ${colors.bg} overflow-hidden`}>
                    {/* Group header */}
                    <div className="px-5 py-3.5 flex items-center justify-between border-b border-inherit">
                      <div className="flex items-center gap-2.5">
                        <div className={`h-2.5 w-2.5 rounded-full ${colors.dot}`} />
                        <h3 className="text-sm font-bold text-slate-800 tracking-tight">
                          {group.label}
                        </h3>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colors.badge}`}>
                        {group.stages.length} etapa{group.stages.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Stages list */}
                    <div className="p-4 space-y-2.5">
                      {group.stages.length === 0 && (
                        <p className="text-xs text-slate-400 italic text-center py-3">
                          No hay etapas en este grupo
                        </p>
                      )}
                      {group.stages.map((stage, index) => (
                        <div
                          key={stage.id}
                          className="group flex items-center justify-between rounded-xl bg-white p-3 shadow-sm border border-slate-100 hover:shadow-md hover:border-slate-200 transition-all duration-200 border-l-4"
                          style={{ borderLeftColor: stage.color }}
                        >
                          <div
                            className="flex items-center flex-1 gap-3 cursor-pointer min-w-0"
                            onClick={() => handleEditStage(stage)}
                            title={`Haz clic para editar "${stage.name}"`}
                          >
                            <div
                              className="h-8 w-8 rounded-xl flex-shrink-0 flex items-center justify-center shadow-sm"
                              style={{ backgroundColor: `${stage.color}20` }}
                            >
                              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-slate-800 group-hover:text-indigo-700 transition-colors truncate">
                                {stage.name}
                              </p>
                              <p className="text-xs text-slate-400 truncate">
                                {stage.description || "Sin descripción"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                            <div className="flex flex-col gap-0.5 mr-1.5 border-r border-slate-100 pr-1.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-300 hover:text-slate-600 hover:bg-slate-50"
                                onClick={() => handleMoveStage(stage, "up")}
                                disabled={index === 0}
                                title="Mover arriba"
                              >
                                <ArrowUp className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-300 hover:text-slate-600 hover:bg-slate-50"
                                onClick={() => handleMoveStage(stage, "down")}
                                disabled={index === group.stages.length - 1}
                                title="Mover abajo"
                              >
                                <ArrowDown className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"
                              onClick={() => handleEditStage(stage)}
                              disabled={loading.delete}
                              title={`Editar etapa "${stage.name}"`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-400 hover:bg-red-50 hover:text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDeleteStage(stage);
                              }}
                              disabled={loading.delete}
                              title={`Eliminar etapa "${stage.name}"`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={`w-full mt-1 border-dashed ${colors.btn} rounded-xl h-9 text-xs font-medium transition-all`}
                        onClick={() => handleOpenFormModal(group.level)}
                        disabled={loading.create}
                        title={`Agregar nueva etapa al grupo ${group.label}`}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Agregar etapa
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-50/50 border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10">
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading.create || loading.delete}
                className="px-6 h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
              >
                Cerrar
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="w-[95vw] sm:max-w-md overflow-hidden p-0 rounded-[24px] border-slate-100 flex flex-col">
          <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4">
            <DialogHeader>
              <div className="flex items-center space-x-3">
                <div className="bg-red-100/80 p-2.5 rounded-xl">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div className="text-left">
                  <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                    Confirmar Eliminación
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500 mt-0.5 sr-only">
                    Confirmación para eliminar la etapa {stageToDelete?.name}.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="p-6 bg-slate-50/50 space-y-4">
            <Alert variant="destructive" className="rounded-xl">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                ¿Estás seguro de que deseas eliminar la etapa{" "}
                <strong>"{stageToDelete?.name}"</strong>?
              </AlertDescription>
            </Alert>

            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div
                  className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: `${stageToDelete?.color}20` }}
                >
                  <div className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: stageToDelete?.color }} />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{stageToDelete?.name}</p>
                  <p className="text-xs text-slate-500">
                    {LEVEL_GROUPS.find((g) => g.stage_types.includes(stageToDelete?.stage_type || ""))?.label}
                    {stageToDelete?.description && ` · ${stageToDelete.description}`}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">Esta acción no se puede deshacer.</p>
          </div>

          <div className="bg-slate-50/50 border-t border-slate-100 p-5 px-6 rounded-b-[24px]">
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setIsDeleteConfirmOpen(false)}
                disabled={loading.delete}
                className="px-6 h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteStage}
                disabled={loading.delete}
                className="px-6 h-10 rounded-lg font-medium"
              >
                {loading.delete ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {isFormModalOpen && (
        <StageFormModal
          isOpen={isFormModalOpen}
          onClose={handleCloseFormModal}
          onSave={handleSaveStage}
          initialStage={
            editingStage
              ? {
                  name: editingStage.name,
                  color: editingStage.color,
                  level: STAGE_TYPE_TO_LEVEL[editingStage.stage_type] || 1,
                  orden: editingStage.order_position,
                  stagnation_config: editingStage.stagnation_config
                    ? {
                        value: editingStage.stagnation_config.value,
                        unit: editingStage.stagnation_config.unit,
                        action: editingStage.stagnation_config.action as any,
                      }
                    : null,
                  visibility_config: editingStage.visibility_config
                    ? {
                        value: editingStage.visibility_config.value,
                        unit: editingStage.visibility_config.unit as any,
                      }
                    : null,
                }
              : undefined
          }
          isEdit={!!editingStage}
          defaultLevel={selectedGroupLevel}
        />
      )}
    </>
  );
}
